import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, type RouterHistory } from "@tanstack/react-router";
import { useState } from "react";
import { ApiError, createBff, type Bff } from "../api/client";
import { I18nProvider } from "../i18n";
import { BrandProvider, TenantBrand } from "./brand";
import { createAppRouter } from "./router";
import { BffProvider, RequireSession } from "./session";

/** An error the user can't fix by trying again (4xx) is shown at once, not retried. */
function retry(failures: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status < 500) return false;
  return failures < 2;
}

export function createQueryClient(retryDelay?: number): QueryClient {
  return new QueryClient({ defaultOptions: { queries: { retry, retryDelay, staleTime: 30_000 } } });
}

export function App({ bff, history, navigate, queries }: {
  bff?: Bff;
  history?: RouterHistory;
  navigate?: (url: string) => void;
  queries?: QueryClient;
}) {
  const [state] = useState(() => ({
    bff: bff ?? createBff(),
    queries: queries ?? createQueryClient(),
    router: createAppRouter(history),
  }));
  return (
    <QueryClientProvider client={state.queries}>
      <BffProvider bff={state.bff} leave={navigate}>
        <BrandProvider>
          {(brand) => (
            <I18nProvider product={brand?.productName}>
              <RequireSession>
                <TenantBrand />
                <RouterProvider router={state.router} />
              </RequireSession>
            </I18nProvider>
          )}
        </BrandProvider>
      </BffProvider>
    </QueryClientProvider>
  );
}
